import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Clapperboard, Upload, Trash2, Pencil, Loader2, ImagePlus, Lock, Power, CheckCircle2, Info
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ['image/gif', 'image/png', 'image/jpeg', 'image/webp'];

const fmtBytes = (b) => (b >= 1024 * 1024 ? `${(b / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

/** Phone frame preview — shows how the media fills a 9:19.5 screen. */
const PhonePreview = ({ src, fit = 'cover', width = 150 }) => (
  <div
    className="relative overflow-hidden bg-black shadow-lg ring-4 ring-gray-900"
    style={{ width, height: width * 2.1, borderRadius: width * 0.14 }}
  >
    {src ? (
      <img src={src} alt="Launch screen preview" className="h-full w-full" style={{ objectFit: fit }} />
    ) : (
      <div className="flex h-full w-full items-center justify-center text-xs text-gray-400 px-4 text-center">
        No launch screen — the app opens straight after the splash
      </div>
    )}
    <div className="absolute left-1/2 top-2 h-1.5 w-12 -translate-x-1/2 rounded-full bg-black/70" />
  </div>
);

const LaunchScreen = () => {
  const [data, setData] = useState({ activeId: null, durationSec: 3, fit: 'cover', showSkip: true, media: [], canEdit: false });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  // Settings form
  const [settings, setSettings] = useState({ durationSec: 3, fit: 'cover', showSkip: true });
  const [savingSettings, setSavingSettings] = useState(false);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [name, setName] = useState('');
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInput = useRef(null);

  // Rename
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const apply = (d) => {
    setData((prev) => ({ ...prev, ...d }));
    setSettings({ durationSec: d.durationSec ?? 3, fit: d.fit ?? 'cover', showSkip: d.showSkip !== false });
  };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/launch-screen`);
      apply({ ...res.data, canEdit: !!res.data?.canEdit });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load the launch screen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const errorText = (e, fallback) =>
    e.response?.status === 413
      ? 'The server rejected the file as too large (413). Ask the server admin to set nginx client_max_body_size to at least 12M.'
      : e.response?.data?.detail || fallback;

  // ---------------- upload ----------------
  const openUpload = () => {
    setFile(null);
    setPreviewUrl(null);
    setName('');
    setFileError('');
    setProgress(0);
    setUploadOpen(true);
  };

  const pickFile = (f) => {
    setFileError('');
    setFile(null);
    setPreviewUrl(null);
    if (!f) return;
    if (!ACCEPT.includes(f.type)) return setFileError('Upload a GIF, PNG, JPG or WebP file');
    if (f.size > MAX_BYTES) return setFileError(`File must be smaller than 10 MB (this one is ${fmtBytes(f.size)})`);
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    if (!name) setName(f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').slice(0, 60));
  };

  const doUpload = async () => {
    if (!file) return setFileError('Choose a file');
    setUploading(true);
    setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name.trim());
      const res = await axios.post(`${API}/launch-screen`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100))
      });
      apply(res.data);
      toast.success('Uploaded — click "Make active" to show it in the app');
      setUploadOpen(false);
    } catch (e) {
      toast.error(errorText(e, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  // ---------------- actions ----------------
  const setActive = async (id) => {
    setBusyId(id || 'off');
    try {
      const res = await axios.patch(`${API}/launch-screen/active`, { id });
      apply(res.data);
      toast.success(res.data?.message || 'Updated');
    } catch (e) {
      toast.error(errorText(e, 'Failed to update'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.name}"?`)) return;
    setBusyId(m.id);
    try {
      const res = await axios.delete(`${API}/launch-screen/${m.id}`);
      apply(res.data);
      toast.success('Deleted');
    } catch (e) {
      toast.error(errorText(e, 'Failed to delete'));
    } finally {
      setBusyId(null);
    }
  };

  const saveRename = async () => {
    if (!renameValue.trim()) return toast.error('Enter a name');
    setBusyId(renaming.id);
    try {
      const res = await axios.put(`${API}/launch-screen/${renaming.id}`, { name: renameValue.trim() });
      apply(res.data);
      setRenaming(null);
      toast.success('Renamed');
    } catch (e) {
      toast.error(errorText(e, 'Failed to rename'));
    } finally {
      setBusyId(null);
    }
  };

  const settingsDirty =
    Number(settings.durationSec) !== Number(data.durationSec) ||
    settings.fit !== data.fit ||
    settings.showSkip !== data.showSkip;
  const durationError = (() => {
    const d = Number(settings.durationSec);
    return !Number.isFinite(d) || d < 1 || d > 15 ? 'Between 1 and 15 seconds' : null;
  })();

  const saveSettings = async () => {
    if (durationError) return toast.error(durationError);
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API}/launch-screen/settings`, {
        durationSec: Number(settings.durationSec),
        fit: settings.fit,
        showSkip: settings.showSkip
      });
      apply(res.data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(errorText(e, 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const active = data.media.find((m) => m.id === data.activeId) || null;
  const canEdit = data.canEdit;

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="launch-screen-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-orange-100 flex items-center justify-center">
            <Clapperboard className="text-orange-600" size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Launch Screen</h1>
            <p className="text-sm text-gray-500">Full-screen GIF or image shown in the customer app right after the splash.</p>
          </div>
        </div>
        {canEdit ? (
          <Button onClick={openUpload} style={{ backgroundColor: '#C2410C' }} data-testid="launch-upload">
            <Upload size={16} className="mr-2" /> Upload GIF / image
          </Button>
        ) : (
          !loading && (
            <Badge className="border-0 bg-gray-100 text-gray-600 w-fit"><Lock size={12} className="mr-1" /> Only Super Admin can make changes</Badge>
          )
        )}
      </div>

      {/* Live status + settings */}
      <Card className="shadow-sm">
        <CardContent className="p-5 grid gap-6 lg:grid-cols-[auto_1fr]">
          <div className="flex justify-center">
            <PhonePreview src={active?.url} fit={settings.fit} />
          </div>
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500">Status</div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`h-2.5 w-2.5 rounded-full ${active ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <span className="text-lg font-semibold">{active ? `Showing "${active.name}"` : 'Off'}</span>
                </div>
                {data.updatedAt && (
                  <div className="text-xs text-gray-500 mt-1">
                    Changed {format(new Date(data.updatedAt), 'dd MMM yyyy, h:mm a')}{data.updatedByName ? ` by ${data.updatedByName}` : ''}
                  </div>
                )}
              </div>
              {canEdit && active && (
                <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                        disabled={busyId === 'off'} onClick={() => setActive(null)} data-testid="launch-turn-off">
                  {busyId === 'off' ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Power size={16} className="mr-2" />}
                  Turn off
                </Button>
              )}
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <Label>Show for (seconds)</Label>
                <Input type="number" min="1" max="15" step="0.5" className="mt-1.5" disabled={!canEdit}
                       value={settings.durationSec}
                       onChange={(e) => setSettings((s) => ({ ...s, durationSec: e.target.value }))} />
                {durationError
                  ? <p className="text-xs text-red-600 mt-1">{durationError}</p>
                  : <p className="text-xs text-gray-500 mt-1">For a GIF, use its full play time.</p>}
              </div>
              <div>
                <Label>Fit to screen</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {[
                    { v: 'cover', label: 'Fill', hint: 'No gaps, edges may crop' },
                    { v: 'contain', label: 'Fit', hint: 'Whole image, may add bars' }
                  ].map((o) => (
                    <button key={o.v} type="button" disabled={!canEdit}
                            onClick={() => setSettings((s) => ({ ...s, fit: o.v }))}
                            className={`rounded-lg border-2 px-2 py-1.5 text-left transition-colors disabled:opacity-60 ${settings.fit === o.v ? 'border-orange-500 bg-orange-50/60' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="text-sm font-medium">{o.label}</div>
                      <div className="text-[11px] text-gray-500 leading-tight">{o.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label>Skip button</Label>
                <div className="mt-1.5 flex items-center gap-3 rounded-lg border px-3 py-2.5">
                  <Switch checked={settings.showSkip} disabled={!canEdit}
                          onCheckedChange={(v) => setSettings((s) => ({ ...s, showSkip: v }))}
                          className="data-[state=checked]:bg-green-600" />
                  <span className="text-sm text-gray-700">{settings.showSkip ? 'Users can skip' : 'No skip'}</span>
                </div>
              </div>
            </div>

            {canEdit && (
              <div className="flex justify-end">
                <Button disabled={!settingsDirty || savingSettings || !!durationError} onClick={saveSettings}
                        style={{ backgroundColor: '#C2410C' }} data-testid="launch-save-settings">
                  {savingSettings && <Loader2 size={16} className="mr-2 animate-spin" />}Save settings
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <Info size={18} className="mt-0.5 shrink-0" />
        <div>
          Changes reach the app within about a minute, without an app update. The file is downloaded in the background,
          so the first time a new file is made active some users may see it on their next app open. Keep files small
          (GIF under 3 MB, image 1080 × 2340) for a fast start.
        </div>
      </div>

      {/* Library */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[1, 2, 3].map((i) => <div key={i} className="h-72 rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : data.media.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="py-14 text-center">
            <Clapperboard className="mx-auto text-gray-300" size={40} />
            <div className="mt-2 font-medium text-gray-700">No launch screens yet</div>
            <div className="text-sm text-gray-500">Upload a GIF or image to show after the splash screen.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {data.media.map((m) => {
            const isActive = m.id === data.activeId;
            const busy = busyId === m.id;
            return (
              <Card key={m.id} className={`shadow-sm overflow-hidden ${isActive ? 'ring-2 ring-orange-500' : ''}`} data-testid={`launch-item-${m.id}`}>
                <div className="relative bg-black" style={{ aspectRatio: '9 / 16' }}>
                  <img src={m.url} alt={m.name} loading="lazy" className="h-full w-full" style={{ objectFit: settings.fit }} />
                  <Badge className="absolute left-2 top-2 border-0 bg-black/60 text-white uppercase text-[10px]">{m.kind}</Badge>
                  {isActive && (
                    <Badge className="absolute right-2 top-2 border-0 bg-green-600 text-white text-[10px]">
                      <CheckCircle2 size={11} className="mr-1" /> Active
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3 space-y-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate text-sm" title={m.name}>{m.name}</div>
                    <div className="text-[11px] text-gray-500">
                      {[m.width && m.height ? `${m.width} × ${m.height}` : null, m.bytes ? fmtBytes(m.bytes) : null].filter(Boolean).join(' • ')}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <Button size="sm" className="flex-1" variant={isActive ? 'outline' : 'default'}
                              disabled={isActive || busy}
                              style={isActive ? undefined : { backgroundColor: '#C2410C' }}
                              onClick={() => setActive(m.id)}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : isActive ? 'Active' : 'Make active'}
                      </Button>
                      <Button size="sm" variant="ghost" title="Rename" disabled={busy}
                              onClick={() => { setRenaming(m); setRenameValue(m.name); }}>
                        <Pencil size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" title={isActive ? 'Turn off before deleting' : 'Delete'}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              disabled={busy || isActive} onClick={() => remove(m)}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => !uploading && setUploadOpen(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload launch screen</DialogTitle>
            <DialogDescription>GIF, PNG, JPG or WebP up to 10 MB. Portrait 1080 × 2340 (9:19.5) fits phones best; keep text away from the edges.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-4 items-start">
              <button type="button" onClick={() => fileInput.current?.click()}
                      className="shrink-0 rounded-2xl border-2 border-dashed border-gray-300 hover:border-orange-400 p-1">
                {previewUrl
                  ? <PhonePreview src={previewUrl} fit={settings.fit} width={120} />
                  : (
                    <div className="flex h-[252px] w-[120px] flex-col items-center justify-center gap-2 text-gray-400">
                      <ImagePlus size={28} />
                      <span className="text-xs">Choose file</span>
                    </div>
                  )}
              </button>
              <div className="flex-1 space-y-3">
                <input ref={fileInput} type="file" accept={ACCEPT.join(',')} className="hidden" data-testid="launch-file"
                       onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
                <Button type="button" variant="outline" onClick={() => fileInput.current?.click()}>
                  <ImagePlus size={16} className="mr-2" /> {file ? 'Choose another file' : 'Choose file'}
                </Button>
                {file && <div className="text-xs text-gray-500">{file.name} • {fmtBytes(file.size)}</div>}
                {file && file.size > 3 * 1024 * 1024 && (
                  <div className="text-xs text-amber-700">Large file — it may not be ready on slow networks. Under 3 MB is recommended.</div>
                )}
                {fileError && <p className="text-sm text-red-600">{fileError}</p>}
                <div>
                  <Label>Name</Label>
                  <Input className="mt-1.5" maxLength={60} value={name} placeholder="e.g. Diwali offer"
                         onChange={(e) => setName(e.target.value)} />
                </div>
              </div>
            </div>
            {uploading && (
              <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${progress}%` }} />
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={uploading} onClick={() => setUploadOpen(false)}>Cancel</Button>
              <Button disabled={uploading || !file} onClick={doUpload} style={{ backgroundColor: '#C2410C' }} data-testid="launch-upload-submit">
                {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                {uploading ? `Uploading ${progress}%` : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Rename</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input maxLength={60} value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRenaming(null)}>Cancel</Button>
              <Button onClick={saveRename} disabled={busyId === renaming?.id} style={{ backgroundColor: '#C2410C' }}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LaunchScreen;
